import { AxiosResponse } from 'axios';

import { api } from '../../http';
import {
  Absence,
  BaseFilter,
  PageRequest,
  PageResponse,
  PersonAbsence,
  PersonAbsencesToDelete,
  TreeNodeBase
} from '../../types/interfaces';

import { ApiResponse } from './interfaces';

export interface ImportResponse {
  absences: Array<{
    id: number;
    from: Date,
    to: Date,
    personId: number;
    departmentId: number;
    absence: { id: number, label: string, shortName: string, shouldRemoveShift: boolean }
  }>;
  errors: string[];
}

export const AbsenceService = {
  getAbsences(
    params: PageRequest & {
      sortBy?: string;
      sortDirection?: string;
      parsedFilters?: BaseFilter[];
      fillers?: string;
    }
  ): Promise<AxiosResponse<ApiResponse<PageResponse<Absence>>>> {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (params[key]) {
        searchParams.append(key, String(params[key]));
      }
    });

    return api.get<ApiResponse<PageResponse<Absence>>>('absence', {
      params: searchParams
    });
  },

  getAbsenceById(id: number): Promise<AxiosResponse<ApiResponse<Absence>>> {
    return api.get<ApiResponse<Absence>>(`absence/${id}`);
  },

  updateAbsence(
    id: number,
    absence: Absence
  ): Promise<AxiosResponse<ApiResponse<Absence>>> {
    return api.put<ApiResponse<Absence>>(`absence/${id}`, absence);
  },

  deleteAbsence(id: number): Promise<AxiosResponse<ApiResponse<null>>> {
    return api.delete<ApiResponse<null>>(`absence/${id}`);
  },

  createAbsence(
    absence: Absence
  ): Promise<AxiosResponse<ApiResponse<Absence>>> {
    return api.post<ApiResponse<Absence>>('absence', absence);
  },

  getAbsencesForSelection(): Promise<
    AxiosResponse<ApiResponse<TreeNodeBase[]>>
  > {
    return api.get<ApiResponse<TreeNodeBase[]>>('absence', {
      params: { ForSelection: 'true' }
    });
  },

  // TODO personAbsences
  getPersonsAbsences(
    departmentId: number,
    date: string
  ): Promise<AxiosResponse<ApiResponse<Record<string, PersonAbsence[]>>>> {
    return api.get<ApiResponse<Record<string, PersonAbsence[]>>>(
      `absence/department/${departmentId}`,
      { params: { date } }
    );
  },

  createPersonAbsence(
    personId: number,
    personAbsence: PersonAbsence,
    validation = false
  ): Promise<AxiosResponse<ApiResponse<PersonAbsence>>> {
    return api.post<ApiResponse<PersonAbsence>>(
      `absence/person/${personId}`,
      personAbsence,
      {
        params: { validation }
      }
    );
  },

  getPersonAbsences(
    personId: number
  ): Promise<AxiosResponse<ApiResponse<PersonAbsence>>> {
    return api.get<ApiResponse<PersonAbsence>>(`absence/person/${personId}`);
  },

  updatePersonAbsence(
    personId: number,
    date: string,
    absence: PersonAbsence
  ): Promise<AxiosResponse<ApiResponse<PersonAbsence>>> {
    return api.put<ApiResponse<PersonAbsence>>(
      `absence/personAbsence/${absence.id}`,
      absence
    );
  },

  deletePersonAbsence(
    personAnsenceId: number
  ): Promise<AxiosResponse<ApiResponse<null>>> {
    return api.delete<ApiResponse<null>>(
      `absence/personAbsence/${personAnsenceId}`
    );
  },

  deletePersonAbsencesInRange(
    absences: PersonAbsencesToDelete
  ): Promise<AxiosResponse<ApiResponse<PersonAbsencesToDelete>>> {
    return api.delete('absence/personAbsence', { data: absences });
  },

  getAbsencesTemplateExcel(): Promise<AxiosResponse<Blob>> {
    return api.get('absencePerson/export/template', {
      responseType: 'blob'
    });
  },

  importAbsences(
    formData: FormData,
    validation = false
  ): Promise<AxiosResponse<ImportResponse>> {
    return api.post<ImportResponse>('absencePerson/import', formData, {
      // query-параметры:
      params: { validation },
      // заголовок для отправки файла:
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
};
